/**
 * The toast stack (lib/toast.ts). Mounted once, beside the app, fixed to the bottom corner of the window — above the
 * dialogs, so a refusal behind an open dialog is still seen. Errors are role="alert" (read out at once), the rest are
 * read politely. Hover or focus holds a toast; «×» closes it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { dismiss, subscribe, type Toast } from '../lib/toast';

function Item({ t }: { t: Toast }) {
  const [held, setHeld] = useState(false);
  const left = useRef(t.ms);
  const started = useRef(Date.now());
  useEffect(() => { left.current = t.ms; }, [t.id, t.ms]);
  useEffect(() => {
    if (held) return;
    started.current = Date.now();
    const timer = setTimeout(() => dismiss(t.id), Math.max(0, left.current));
    return () => { clearTimeout(timer); left.current -= Date.now() - started.current; };
  }, [held, t.id]);
  const tone = t.tone || 'info';
  return (
    <div className={'toast ' + tone} role={t.tone === 'err' ? 'alert' : 'status'}
         onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}
         onFocus={() => setHeld(true)} onBlur={() => setHeld(false)}>
      <span className="toast-ico" aria-hidden="true">{t.tone === 'ok' ? '✓' : t.tone === 'err' ? '!' : t.tone === 'warn' ? '!' : 'i'}</span>
      <span className="toast-text">{t.text}</span>
      <button type="button" className="toast-x" aria-label="Lukk meldingen" onClick={() => dismiss(t.id)}>×</button>
    </div>
  );
}

export function Toaster() {
  const [items, setItems] = useState<Toast[]>([]);
  useEffect(() => subscribe(setItems), []);
  return (
    <div className="toaster" aria-live="polite" aria-relevant="additions text">
      {items.map((t) => <Item key={t.id} t={t} />)}
    </div>
  );
}
